import moment from "moment";
import {
  TRIPTYPEARRAY,
  NEWTRIPTYPEARRAY,
  CABBINCLASSARRAY,
  NEWCABBINCLASSARRAY,
  FARETYPEARRAY,
} from "../_constants/constant";
import airports from "../_constants/airports.json";
import {
  getMinPrice,
  getNewMinPrice,
  calculateTotalJourneyDuration,
  calculateNewTotalJourneyDuration,
} from "./fareUtils";

export const getFlightImage = (flightCode: string) => {
  return `https://images.skyruner.com/Uploads/AirlineLogo/${flightCode}.png`;
};

const getKeyOrValue = (
  array: any[],
  keyName: string,
  valueName: string,
  input: string
) => {
  const foundObj = array.find(
    (item: any) => item[keyName] === input || item[valueName] === input
  );
  if (!foundObj) return "";
  return foundObj[keyName] === input ? foundObj[valueName] : foundObj[keyName];
};

const getKeyOrValueNew = (
  array: any[],
  keyName: string,
  valueName: string,
  input: string
) => {
  const foundObj = array.find(
    (item: any) => item[keyName] === input || item[valueName] === input
  );
  if (!foundObj) return "";
  return foundObj[valueName];
};

export const getTripType = (input: string) => {
  return getKeyOrValue(TRIPTYPEARRAY, "key", "type", input);
};

export const getNewTripType = (input: string) => {
  return getKeyOrValueNew(NEWTRIPTYPEARRAY, "key", "type", input);
};

export const getCabinClass = (input: string) => {
  return getKeyOrValue(CABBINCLASSARRAY, "key", "class", input);
};

export const getNewCabinClass = (input: string) => {
  return getKeyOrValueNew(NEWCABBINCLASSARRAY, "key", "class", input);
};

export const getFareType = (input: string) => {
  return getKeyOrValue(FARETYPEARRAY, "key", "type", input);
};

export const getCityNameByCityCode = (
  flightplace_code: any,
  flightPlaces: any
) => {
  const flightPlace: any[] = flightPlaces?.filter(
    (e: any) => e?.flightplace_code === flightplace_code
  );
  const city_name = flightPlace[0]?.city_name;
  return city_name;
};

export const getAirportNameByCityCode = (
  flightplace_code: any,
  flightPlaces: any
) => {
  const flightPlace: any[] = flightPlaces?.filter(
    (e: any) => e?.flightplace_code === flightplace_code
  );
  const flightplace_name = flightPlace[0]?.flightplace_name;
  return flightplace_name;
};

// Convert the JSON array into a lookup map for faster access
const airportMap = airports.reduce((map: any, airport: any) => {
  map[airport.Value] = airport.CountryCode;
  return map;
}, {} as Record<string, string>);

export const getCountryCode = (iataCode: string): string | undefined => {
  return airportMap[iataCode];
};

export const getTravelType = (
  departureCode: string,
  arrivalCode: string
): number => {
  const departureCountry = getCountryCode(departureCode);
  const arrivalCountry = getCountryCode(arrivalCode);
  if (!departureCountry || !arrivalCountry) {
    throw new Error("Invalid airport codes");
  }
  return departureCountry === arrivalCountry ? 0 : 1;
};

export const isInternationalFlight = (segments: any[]) => {
  for (const flight of segments) {
    const originCountryCode = getCountryCode(flight.Origin);
    const destinationCountryCode = getCountryCode(flight.Destination);
    if (!originCountryCode || !destinationCountryCode) {
      console.warn(
        `Unknown airport code: ${flight.Origin} or ${flight.Destination}`
      );
      continue;
    }
    if (originCountryCode !== destinationCountryCode) {
      return true;
    }
  }
  return false;
};

export const groupComboFlights = (comboFlights: any[] = []) => {
  const groupedFlights: any[] = [];
  let currentGroup: any[] = [];
  comboFlights?.forEach((combo) => {
    let currentRoute: any = null;
    combo.sI?.forEach((flight: any) => {
      if (flight.sN === 0) {
        if (currentRoute) {
          currentGroup.push(currentRoute);
        }
        currentRoute = { ...combo, sI: [flight] };
      } else if (currentRoute) {
        currentRoute.sI.push(flight);
      }
    });
    if (currentRoute) {
      currentGroup.push(currentRoute);
    }
    groupedFlights.push(currentGroup);
    currentGroup = [];
  });
  return groupedFlights;
};

export const getDepartureAndArivalCityByRoute = (groupedRoutes: any[]) => {
  return groupedRoutes
    .map((route) => {
      if (!route) return null;
      const departureCode = route?.sI[0]?.da?.code || "";
      const arrivalCode = route?.sI[route?.sI?.length - 1]?.aa?.code || "";
      const departureCity = route?.sI[0]?.da?.city || "";
      const arrivalCity = route?.sI[route?.sI?.length - 1]?.aa?.city || "";
      return {
        departureCode,
        arrivalCode,
        departureCity,
        arrivalCity,
      };
    })
    .filter(Boolean);
};

export const filterFlights = (flights: any = [], filter: any) => {
  let updatedFlights = [...flights];
  if (filter?.stop && filter?.stop?.length > 0) {
    updatedFlights = updatedFlights.filter((flight) => {
      const stops = flight?.sI?.length - 1;
      return (
        filter.stop.includes(stops) || (stops >= 2 && filter?.stop?.includes(2))
      );
    });
  }
  if (filter?.airline && filter?.airline?.length > 0) {
    updatedFlights = updatedFlights.filter((flight) => {
      return flight.sI.some((segment: any) =>
        filter?.airline?.includes(segment.fD.aI.name)
      );
    });
  }
  if (filter?.departureTime && filter?.departureTime?.length > 0) {
    updatedFlights = updatedFlights.filter((flight) => {
      const hour = moment(flight.sI[0]?.dt).hour();
      return filter?.departureTime.some((timeRange: any) => {
        switch (timeRange) {
          case "before6am":
            return hour < 6;
          case "6am-12pm":
            return hour >= 6 && hour < 12;
          case "12pm-6pm":
            return hour >= 12 && hour < 18;
          case "after6pm":
            return hour >= 18;
          default:
            return true;
        }
      });
    });
  }
  if (filter?.arrivalTime && filter?.arrivalTime?.length > 0) {
    updatedFlights = updatedFlights.filter((flight) => {
      const hour = moment(flight.sI[flight?.sI?.length - 1]?.at).hour();
      return filter?.arrivalTime.some((timeRange: any) => {
        switch (timeRange) {
          case "before6am":
            return hour < 6;
          case "6am-12pm":
            return hour >= 6 && hour < 12;
          case "12pm-6pm":
            return hour >= 12 && hour < 18;
          case "after6pm":
            return hour >= 18;
          default:
            return true;
        }
      });
    });
  }
  return updatedFlights;
};

export const filterNewFlights = (flights: any = [], filter: any) => {
  let updatedFlights = [...flights];
  if (filter?.stop && filter?.stop?.length > 0) {
    updatedFlights = updatedFlights.filter((flight) => {
      const stops = flight?.Segments?.length - 1;
      return (
        filter.stop.includes(stops) || (stops >= 2 && filter?.stop?.includes(2))
      );
    });
  }
  if (filter?.airline && filter?.airline?.length > 0) {
    updatedFlights = updatedFlights.filter((flight) => {
      return flight.Segments?.some((segment: any) =>
        filter?.airline?.includes(segment?.Airline_Name)
      );
    });
  }

  if (filter?.farePolicy && filter.farePolicy.length > 0) {
    updatedFlights = updatedFlights
      .map((flight) => {
        const filteredFares = flight.Fares?.filter((fare: any) => {
          const isRefundable = fare.Refundable;
          if (
            filter.farePolicy.includes("Refundable") &&
            filter.farePolicy.includes("NonRefundable")
          ) {
            return true;
          }
          if (filter.farePolicy.includes("Refundable")) {
            return isRefundable;
          }
          if (filter.farePolicy.includes("NonRefundable")) {
            return !isRefundable;
          }
          return false;
        });

        if (filteredFares?.length > 0) {
          return { ...flight, Fares: filteredFares };
        }
        return null;
      })
      .filter(Boolean);
  }

  if (filter?.departureTime && filter?.departureTime?.length > 0) {
    updatedFlights = updatedFlights.filter((flight) => {
      const hour = moment(flight.Segments[0]?.Departure_DateTime).hour();
      return filter?.departureTime.some((timeRange: any) => {
        switch (timeRange) {
          case "before6am":
            return hour < 6;
          case "6am-12pm":
            return hour >= 6 && hour < 12;
          case "12pm-6pm":
            return hour >= 12 && hour < 18;
          case "after6pm":
            return hour >= 18;
          default:
            return true;
        }
      });
    });
  }
  if (filter?.arrivalTime && filter?.arrivalTime?.length > 0) {
    updatedFlights = updatedFlights.filter((flight) => {
      const hour = moment(
        flight.Segments[flight?.Segments?.length - 1]?.Arrival_DateTime
      ).hour();
      return filter?.arrivalTime.some((timeRange: any) => {
        switch (timeRange) {
          case "before6am":
            return hour < 6;
          case "6am-12pm":
            return hour >= 6 && hour < 12;
          case "12pm-6pm":
            return hour >= 12 && hour < 18;
          case "after6pm":
            return hour >= 18;
          default:
            return true;
        }
      });
    });
  }
  if (filter?.priceRange) {
    const { min, max } = filter.priceRange;
    updatedFlights = updatedFlights.filter((flight) => {
      const minFlightPrice = getNewMinPrice(flight.Fares);
      return minFlightPrice >= min && minFlightPrice <= max;
    });
  }
  return updatedFlights;
};

// export const filterNewFlights = (flights: any = [], filter: any) => {
//   let updatedFlights = [...flights];

//   if (filter?.stop && filter?.stop?.length > 0) {
//     updatedFlights = updatedFlights.filter((flight) => {
//       const stops = flight?.Segments?.length - 1;
//       return (
//         filter.stop.includes(stops) || (stops >= 2 && filter?.stop?.includes(2))
//       );
//     });
//   }

//   if (filter?.airline && filter?.airline?.length > 0) {
//     updatedFlights = updatedFlights.filter((flight) => {
//       return flight.Segments?.some((segment: any) =>
//         filter?.airline?.includes(segment?.Airline_Name)
//       );
//     });
//   }

//   if (filter?.departureTime && filter?.departureTime?.length > 0) {
//     updatedFlights = updatedFlights.filter((flight) => {
//       const hour = moment(flight.Segments[0]?.Departure_DateTime).hour();
//       return filter?.departureTime.some((timeRange: any) => {
//         switch (timeRange) {
//           case "before6am":
//             return hour < 6;
//           case "6am-12pm":
//             return hour >= 6 && hour < 12;
//           case "12pm-6pm":
//             return hour >= 12 && hour < 18;
//           case "after6pm":
//             return hour >= 18;
//           default:
//             return true;
//         }
//       });
//     });
//   }

//   if (filter?.arrivalTime && filter?.arrivalTime?.length > 0) {
//     updatedFlights = updatedFlights.filter((flight) => {
//       const hour = moment(
//         flight.Segments[flight?.Segments?.length - 1]?.Arrival_DateTime
//       ).hour();
//       return filter?.arrivalTime.some((timeRange: any) => {
//         switch (timeRange) {
//           case "before6am":
//             return hour < 6;
//           case "6am-12pm":
//             return hour >= 6 && hour < 12;
//           case "12pm-6pm":
//             return hour >= 12 && hour < 18;
//           case "after6pm":
//             return hour >= 18;
//           default:
//             return true;
//         }
//       });
//     });
//   }

//   if (filter?.priceRange) {
//     const { min, max } = filter.priceRange;
//     updatedFlights = updatedFlights.filter((flight) => {
//       const minFlightPrice = getNewMinPrice(flight.Fares);
//       return minFlightPrice >= min && minFlightPrice <= max;
//     });
//   }

//   if (filter?.farePolicy && filter.farePolicy.length > 0) {
//     updatedFlights = updatedFlights
//       .map((flight) => {
//         const filteredFares = flight.Fares?.filter((fare: any) => {
//           const isRefundable = fare.Refundable;
//           if (
//             filter.farePolicy.includes("Refundable") &&
//             filter.farePolicy.includes("NonRefundable")
//           ) {
//             return true;
//           }
//           if (filter.farePolicy.includes("Refundable")) {
//             return isRefundable;
//           }
//           if (filter.farePolicy.includes("NonRefundable")) {
//             return !isRefundable;
//           }
//           return false;
//         });

//         if (filteredFares?.length > 0) {
//           return { ...flight, Fares: filteredFares };
//         }
//         return null;
//       })
//       .filter(Boolean);
//   }

//   return updatedFlights;
// };

export const filterComboFlights = (
  groupedFlights: any[] = [],
  comboFilters: any[] = []
) => {
  return groupedFlights
    .map((group: any) => {
      return group.filter((flight: any, index: number) => {
        const filter = comboFilters[index] || {};
        const totalStops = flight.sI.length - 1;
        if (filter.stop?.length && !filter.stop.includes(totalStops)) {
          return false;
        }
        if (filter.airline && filter.airline.length > 0) {
          const hasValidAirline = flight.sI.some((segment: any) =>
            filter.airline.includes(segment.fD.aI.name)
          );
          if (!hasValidAirline) return false;
        }
        if (filter.departureTime && filter.departureTime.length > 0) {
          const departureHour = moment(flight.sI[0]?.dt).hour();
          const isDepartureValid = filter.departureTime.some(
            (timeRange: any) => {
              switch (timeRange) {
                case "before6am":
                  return departureHour < 6;
                case "6am-12pm":
                  return departureHour >= 6 && departureHour < 12;
                case "12pm-6pm":
                  return departureHour >= 12 && departureHour < 18;
                case "after6pm":
                  return departureHour >= 18;
                default:
                  return true;
              }
            }
          );
          if (!isDepartureValid) return false;
        }
        if (filter.arrivalTime && filter.arrivalTime.length > 0) {
          const arrivalHour = moment(
            flight.sI[flight.sI.length - 1]?.at
          ).hour();
          const isArrivalValid = filter.arrivalTime.some((timeRange: any) => {
            switch (timeRange) {
              case "before6am":
                return arrivalHour < 6;
              case "6am-12pm":
                return arrivalHour >= 6 && arrivalHour < 12;
              case "12pm-6pm":
                return arrivalHour >= 12 && arrivalHour < 18;
              case "after6pm":
                return arrivalHour >= 18;
              default:
                return true;
            }
          });
          if (!isArrivalValid) return false;
        }
        return true;
      });
    })
    .filter((group) =>
      group.length >=
      ((groupedFlights?.length > 0 && groupedFlights[0]?.length) || 1)
        ? true
        : false
    );
};

export const sortFlights = (flightsToSort: any = [], sortType: any) => {
  if (!sortType || !sortType.field || !sortType.order) {
    return flightsToSort;
  }
  const { field, order } = sortType;
  return flightsToSort.sort((a: any, b: any) => {
    let comparison = 0;
    if (field === "price") {
      const priceA = getMinPrice(a.totalPriceList);
      const priceB = getMinPrice(b.totalPriceList);
      comparison = priceA - priceB;
    } else if (field === "duration") {
      const durationA = calculateTotalJourneyDuration(a.sI);
      const durationB = calculateTotalJourneyDuration(b.sI);
      comparison = durationA - durationB;
    } else if (field === "departure") {
      const departureA: any = moment(a.sI[0].dt);
      const departureB: any = moment(b.sI[0].dt);
      comparison = departureA - departureB;
    } else if (field === "arrival") {
      const arrivalA: any = moment(a.sI[a.sI.length - 1].at);
      const arrivalB: any = moment(b.sI[b.sI.length - 1].at);
      comparison = arrivalA - arrivalB;
    }
    return order === "asc" ? comparison : -comparison;
  });
};

export const sortNewFlights = (flightsToSort: any = [], sortType: any) => {
  if (!sortType || !sortType.field || !sortType.order) {
    return flightsToSort;
  }
  const { field, order } = sortType;
  return flightsToSort.sort((a: any, b: any) => {
    let comparison = 0;
    if (field === "price") {
      const priceA = getNewMinPrice(a.Fares);
      const priceB = getNewMinPrice(b.Fares);
      comparison = priceA - priceB;
    } else if (field === "duration") {
      const durationA = calculateNewTotalJourneyDuration(a.Segments);
      const durationB = calculateNewTotalJourneyDuration(b.Segments);
      comparison = durationA - durationB;
    } else if (field === "departure") {
      const departureA: any = moment(a.Segments[0].Departure_DateTime);
      const departureB: any = moment(b.Segments[0].Departure_DateTime);
      comparison = departureA - departureB;
    } else if (field === "arrival") {
      const arrivalA: any = moment(
        a.Segments[a.Segments.length - 1].Arrival_DateTime
      );
      const arrivalB: any = moment(
        b.Segments[b.Segments.length - 1].Arrival_DateTime
      );
      comparison = arrivalA - arrivalB;
    }
    return order === "asc" ? comparison : -comparison;
  });
};

export const sortComboFlights = (
  groupedFlights: any[] = [],
  comboSortType: any = {},
  activeIndex: number
) => {
  if (!comboSortType || !comboSortType.field || !comboSortType.order) {
    return groupedFlights;
  }
  const { field, order } = comboSortType;
  return [...groupedFlights].sort((groupA, groupB) => {
    const flightA = groupA[activeIndex] || groupA[0];
    const flightB = groupB[activeIndex] || groupB[0];
    return compareFlights(flightA, flightB, field, order);
  });
};

const compareFlights = (a: any, b: any, field: any, order: any) => {
  let comparison = 0;
  if (field === "price") {
    const priceA = getMinPrice(a.totalPriceList);
    const priceB = getMinPrice(b.totalPriceList);
    comparison = priceA - priceB;
  } else if (field === "duration") {
    const durationA = calculateTotalJourneyDuration(a.sI);
    const durationB = calculateTotalJourneyDuration(b.sI);
    comparison = durationA - durationB;
  } else if (field === "departure") {
    const departureA: any = moment(a.sI[0].dt);
    const departureB: any = moment(b.sI[0].dt);
    comparison = departureA - departureB;
  } else if (field === "arrival") {
    const arrivalA: any = moment(a.sI[a.sI.length - 1].at);
    const arrivalB: any = moment(b.sI[b.sI.length - 1].at);
    comparison = arrivalA - arrivalB;
  }
  return order === "asc" ? comparison : -comparison;
};

export const generateFilterOptions = (
  flights: any,
  flightSearchDetails: any
) => {
  const stopGroups: any = {};
  const airlineGroups: any = {};
  flights?.forEach((flight: any) => {
    const stops = flight?.sI?.length - 1;
    const airlineName = flight?.sI[0]?.fD?.aI?.name;
    const minPrice = flight?.totalPriceList?.reduce((min: any, price: any) => {
      const totalAdultPrice =
        (price?.fd?.ADULT?.fC?.TF || 0) * flightSearchDetails.ADULT;
      const totalChildPrice =
        (price?.fd?.CHILD?.fC?.TF || 0) * flightSearchDetails.CHILD;
      const totalInfantPrice =
        (price?.fd?.INFANT?.fC?.TF || 0) * flightSearchDetails.INFANT;
      const totalPrice = totalAdultPrice + totalChildPrice + totalInfantPrice;
      return Math.min(min, totalPrice);
    }, Infinity);
    if (stopGroups[stops]) {
      stopGroups[stops].count += 1;
      stopGroups[stops].minPrice = Math.min(
        stopGroups[stops].minPrice,
        minPrice
      );
    } else {
      stopGroups[stops] = { count: 1, minPrice };
    }
    if (airlineGroups[airlineName]) {
      airlineGroups[airlineName].count += 1;
      airlineGroups[airlineName].minPrice = Math.min(
        airlineGroups[airlineName].minPrice,
        minPrice
      );
    } else {
      airlineGroups[airlineName] = { count: 1, minPrice };
    }
  });
  const stopFilterOptions = Object.keys(stopGroups).reduce(
    (acc: any, stops: any) => {
      const { count, minPrice } = stopGroups[stops];
      let label;
      let value;
      if (stops === "0") {
        label = `Non-stop (${count})`;
        value = 0;
      } else if (stops === "1") {
        label = `1 stop (${count})`;
        value = 1;
      } else {
        const existing: any = acc.find((option: any) => option.value === 2);
        if (existing) {
          existing.count += count;
          existing.minPrice = Math.min(existing.minPrice, minPrice);
        } else {
          acc.push({
            label: `2+ stops (${count})`,
            count: count,
            minPrice: minPrice,
            value: 2,
          });
        }
        return acc;
      }
      acc.push({ label, count, minPrice, value });
      return acc;
    },
    []
  );
  const twoPlusStops = stopFilterOptions.find(
    (option: any) => option.value === 2
  );
  if (twoPlusStops) {
    twoPlusStops.label = `2+ stops (${twoPlusStops.count})`;
  }
  const airlineFilterOptions = Object.keys(airlineGroups).map((airlineName) => {
    const { count, minPrice } = airlineGroups[airlineName];
    const label = `${airlineName} (${count})`;
    return { label, count, minPrice, value: airlineName };
  });
  return { stopFilterOptions, airlineFilterOptions };
};

// export const generateNewFilterOptions = (
//   flights: any,
//   flightSearchDetails: any
// ) => {
//   const stopGroups: any = {};
//   const airlineGroups: any = {};
//   let minPrice = Infinity;
//   let maxPrice = 0;

//   flights?.forEach((flight: any) => {
//     const flightMinPrice = getNewMinPrice(flight.Fares);
//     minPrice = Math.min(minPrice, flightMinPrice);
//     maxPrice = Math.max(maxPrice, flightMinPrice);

//     const stops = flight?.Segments?.length - 1;
//     const airlineName = flight?.Segments[0]?.Airline_Name;

//     if (stopGroups[stops]) {
//       stopGroups[stops].count += 1;
//       stopGroups[stops].minPrice = Math.min(
//         stopGroups[stops].minPrice,
//         flightMinPrice
//       );
//     } else {
//       stopGroups[stops] = { count: 1, minPrice: flightMinPrice };
//     }

//     if (airlineGroups[airlineName]) {
//       airlineGroups[airlineName].count += 1;
//       airlineGroups[airlineName].minPrice = Math.min(
//         airlineGroups[airlineName].minPrice,
//         flightMinPrice
//       );
//     } else {
//       airlineGroups[airlineName] = { count: 1, minPrice: flightMinPrice };
//     }
//   });

//   if (minPrice === Infinity) {
//     minPrice = 0;
//   }
//   if (maxPrice === 0 && minPrice === Infinity) {
//     maxPrice = 0;
//   }

//   const stopFilterOptions = Object.keys(stopGroups).reduce(
//     (acc: any, stops: any) => {
//       const { count, minPrice } = stopGroups[stops];
//       let label;
//       let value;

//       if (stops === "0") {
//         label = `Non-stop (${count})`;
//         value = 0;
//       } else if (stops === "1") {
//         label = `1 stop (${count})`;
//         value = 1;
//       } else {
//         const existing: any = acc.find((option: any) => option.value === 2);
//         if (existing) {
//           existing.count += count;
//           existing.minPrice = Math.min(existing.minPrice, minPrice);
//         } else {
//           acc.push({
//             label: `2+ stops (${count})`,
//             count: count,
//             minPrice: minPrice,
//             value: 2,
//           });
//         }
//         return acc;
//       }

//       acc.push({ label, count, minPrice, value });
//       return acc;
//     },
//     []
//   );

//   const twoPlusStops = stopFilterOptions.find(
//     (option: any) => option.value === 2
//   );
//   if (twoPlusStops) {
//     twoPlusStops.label = `2+ stops (${twoPlusStops.count})`;
//   }

//   const airlineFilterOptions = Object.keys(airlineGroups).map((airlineName) => {
//     const { count, minPrice } = airlineGroups[airlineName];
//     const label = `${airlineName} (${count})`;
//     return { label, count, minPrice, value: airlineName };
//   });

//   airlineFilterOptions.sort((a, b) => a.label.localeCompare(b.label));

//   return {
//     stopFilterOptions,
//     airlineFilterOptions,
//     priceRange: {
//       min: minPrice,
//       max: maxPrice,
//       selectedMin: minPrice,
//       selectedMax: maxPrice,
//     },
//   };
// };

export const generateNewFilterOptions = (
  flights: any,
  flightSearchDetails: any
) => {
  const stopGroups: any = {};
  const airlineGroups: any = {};
  const farePolicyGroups: any = {
    Refundable: { count: 0, minPrice: Infinity },
    NonRefundable: { count: 0, minPrice: Infinity },
  };
  let minPrice = Infinity;
  let maxPrice = 0;

  flights?.forEach((flight: any) => {
    let flightMinPrice = Infinity;
    let hasRefundable = false;
    let hasNonRefundable = false;
    let refundableMinPrice = Infinity;
    let nonRefundableMinPrice = Infinity;

    flight.Fares?.forEach((fare: any) => {
      const isRefundable = fare.Refundable;
      const farePrice = fare.FareDetails?.[0]?.Total_Amount || Infinity;

      if (isRefundable) {
        hasRefundable = true;
        refundableMinPrice = Math.min(refundableMinPrice, farePrice);
      } else {
        hasNonRefundable = true;
        nonRefundableMinPrice = Math.min(nonRefundableMinPrice, farePrice);
      }

      flightMinPrice = Math.min(flightMinPrice, farePrice);
    });

    // Update fare policy groups with counts and min prices
    if (hasRefundable) {
      farePolicyGroups.Refundable.count++;
      farePolicyGroups.Refundable.minPrice = Math.min(
        farePolicyGroups.Refundable.minPrice,
        refundableMinPrice
      );
    }
    if (hasNonRefundable) {
      farePolicyGroups.NonRefundable.count++;
      farePolicyGroups.NonRefundable.minPrice = Math.min(
        farePolicyGroups.NonRefundable.minPrice,
        nonRefundableMinPrice
      );
    }

    minPrice = Math.min(minPrice, flightMinPrice);
    maxPrice = Math.max(maxPrice, flightMinPrice);

    // Existing stop and airline grouping logic
    const stops = flight?.Segments?.length - 1;
    const airlineName = flight?.Segments[0]?.Airline_Name;

    if (stopGroups[stops]) {
      stopGroups[stops].count += 1;
      stopGroups[stops].minPrice = Math.min(
        stopGroups[stops].minPrice,
        flightMinPrice
      );
    } else {
      stopGroups[stops] = { count: 1, minPrice: flightMinPrice };
    }

    if (airlineGroups[airlineName]) {
      airlineGroups[airlineName].count += 1;
      airlineGroups[airlineName].minPrice = Math.min(
        airlineGroups[airlineName].minPrice,
        flightMinPrice
      );
    } else {
      airlineGroups[airlineName] = { count: 1, minPrice: flightMinPrice };
    }
  });

  // Handle min/max price edge cases
  if (minPrice === Infinity) minPrice = 0;
  if (maxPrice === 0 && minPrice === Infinity) maxPrice = 0;

  // Create stop filter options
  const stopFilterOptions = Object.keys(stopGroups).reduce(
    (acc: any, stops: any) => {
      const { count, minPrice } = stopGroups[stops];
      let label, value;

      if (stops === "0") {
        label = `Non-stop (${count})`;
        value = 0;
      } else if (stops === "1") {
        label = `1 stop (${count})`;
        value = 1;
      } else {
        const existing = acc.find((option: any) => option.value === 2);
        if (existing) {
          existing.count += count;
          existing.minPrice = Math.min(existing.minPrice, minPrice);
        } else {
          acc.push({
            label: `2+ stops (${count})`,
            count,
            minPrice,
            value: 2,
          });
        }
        return acc;
      }

      acc.push({ label, count, minPrice, value });
      return acc;
    },
    []
  );

  // Update 2+ stops label if exists
  const twoPlusStops = stopFilterOptions.find(
    (option: any) => option.value === 2
  );
  if (twoPlusStops) twoPlusStops.label = `2+ stops (${twoPlusStops.count})`;

  // Create airline filter options
  const airlineFilterOptions = Object.keys(airlineGroups).map((airlineName) => {
    const { count, minPrice } = airlineGroups[airlineName];
    return {
      label: `${airlineName} (${count})`,
      value: airlineName,
      count,
      minPrice,
    };
  });

  // Create fare policy options with min prices
  const farePolicyOptions = [
    {
      label: `Refundable (${farePolicyGroups.Refundable.count})`,
      value: "Refundable",
      count: farePolicyGroups.Refundable.count,
      minPrice:
        farePolicyGroups.Refundable.minPrice !== Infinity
          ? farePolicyGroups.Refundable.minPrice
          : 0,
    },
    {
      label: `Non Refundable (${farePolicyGroups.NonRefundable.count})`,
      value: "NonRefundable",
      count: farePolicyGroups.NonRefundable.count,
      minPrice:
        farePolicyGroups.NonRefundable.minPrice !== Infinity
          ? farePolicyGroups.NonRefundable.minPrice
          : 0,
    },
  ];

  // Sort airlines alphabetically
  airlineFilterOptions.sort((a, b) => a.label.localeCompare(b.label));

  return {
    stopFilterOptions,
    airlineFilterOptions,
    farePolicyOptions,
    priceRange: {
      min: minPrice,
      max: maxPrice,
      selectedMin: minPrice,
      selectedMax: maxPrice,
    },
  };
};

export const generateComboFilterOptions = (
  groupedFlights: any[],
  flightSearchDetails: any
) => {
  const filterOptions: any[] = [];
  const routeCount = Math.max(
    ...groupedFlights.map((flightGroup) => flightGroup.length)
  );

  for (let routeIndex = 0; routeIndex < routeCount; routeIndex++) {
    const stopGroups: any = {};
    const airlineGroups: any = {};
    const farePolicyGroups: any = { Refundable: 0, NonRefundable: 0 };
    let minRefundablePrice = Infinity;
    let minNonRefundablePrice = Infinity;

    groupedFlights?.forEach((flightGroup) => {
      const route = flightGroup[routeIndex];
      if (route) {
        const stops = route?.sI?.length - 1;
        const airlineName = route?.sI[0]?.fD?.aI?.name;
        let hasRefundable = false;
        let hasNonRefundable = false;

        const minPrice =
          route?.totalPriceList?.reduce((min: any, price: any) => {
            const totalAdultPrice =
              (price?.fd?.ADULT?.fC?.TF || 0) * flightSearchDetails.ADULT;
            const totalChildPrice =
              (price?.fd?.CHILD?.fC?.TF || 0) * flightSearchDetails.CHILD;
            const totalInfantPrice =
              (price?.fd?.INFANT?.fC?.TF || 0) * flightSearchDetails.INFANT;
            const totalPrice =
              totalAdultPrice + totalChildPrice + totalInfantPrice;

            // Check fare policy
            if (price?.Refundable) {
              hasRefundable = true;
              minRefundablePrice = Math.min(minRefundablePrice, totalPrice);
            } else {
              hasNonRefundable = true;
              minNonRefundablePrice = Math.min(
                minNonRefundablePrice,
                totalPrice
              );
            }

            return Math.min(min, totalPrice);
          }, Infinity) || 0;

        // Update counts
        if (hasRefundable) farePolicyGroups.Refundable++;
        if (hasNonRefundable) farePolicyGroups.NonRefundable++;

        // Update stop groups
        if (stopGroups[stops]) {
          stopGroups[stops].count += 1;
          stopGroups[stops].minPrice = Math.min(
            stopGroups[stops].minPrice,
            minPrice
          );
        } else {
          stopGroups[stops] = { count: 1, minPrice };
        }

        // Update airline groups
        if (airlineGroups[airlineName]) {
          airlineGroups[airlineName].count += 1;
          airlineGroups[airlineName].minPrice = Math.min(
            airlineGroups[airlineName].minPrice,
            minPrice
          );
        } else {
          airlineGroups[airlineName] = { count: 1, minPrice };
        }
      }
    });

    // Create stop filter options
    const stopFilterOptions = Object.keys(stopGroups).reduce(
      (acc: any, stops: any) => {
        const { count, minPrice } = stopGroups[stops];
        let label;
        let value;
        if (stops === "0") {
          label = `Non-stop (${count})`;
          value = 0;
        } else if (stops === "1") {
          label = `1 stop (${count})`;
          value = 1;
        } else {
          const existing: any = acc.find((option: any) => option.value === 2);
          if (existing) {
            existing.count += count;
            existing.minPrice = Math.min(existing.minPrice, minPrice);
          } else {
            acc.push({
              label: `2+ stops (${count})`,
              count: count,
              minPrice: minPrice,
              value: 2,
            });
          }
          return acc;
        }
        acc.push({ label, count, minPrice, value });
        return acc;
      },
      []
    );

    // Handle 2+ stops label
    const twoPlusStops = stopFilterOptions.find(
      (option: any) => option.value === 2
    );
    if (twoPlusStops) {
      twoPlusStops.label = `2+ stops (${twoPlusStops.count})`;
    }

    // Create airline filter options
    const airlineFilterOptions = Object.keys(airlineGroups).map(
      (airlineName) => {
        const { count, minPrice } = airlineGroups[airlineName];
        const label = `${airlineName} (${count})`;
        return { label, count, minPrice, value: airlineName };
      }
    );

    // Create fare policy options
    const farePolicyOptions = [
      {
        label: `Refundable (${farePolicyGroups.Refundable})`,
        value: "Refundable",
        minPrice: farePolicyGroups.Refundable > 0 ? minRefundablePrice : 0,
        count: farePolicyGroups.Refundable,
      },
      {
        label: `Non-refundable (${farePolicyGroups.NonRefundable})`,
        value: "NonRefundable",
        minPrice:
          farePolicyGroups.NonRefundable > 0 ? minNonRefundablePrice : 0,
        count: farePolicyGroups.NonRefundable,
      },
    ];

    filterOptions.push({
      stopFilterOptions,
      airlineFilterOptions,
      farePolicyOptions,
    });
  }

  return filterOptions;
};
